import React, { createContext, useContext, useState, useEffect } from 'react';

const LangContext = createContext();

export const useLang = () => useContext(LangContext);

const en = {
  nav: {
    services: "Services", outdoor: "Outdoor Living", permitting: "Planning & Permitting",
    visualizer: "AI Visualizer", projects: "Projects", contact: "Contact", estimate: "Get an Estimate",
    technology: "Technology",
  },
  hero: {
    badge: "Construction · Remodeling · Planning · Permitting",
    heading1: "MIAMI", heading2: "MULTI-TRADE",
    tagline: "ONE CONTRACTOR. MULTIPLE TRADES. INSIDE & OUT.",
    desc: "From complete renovations to outdoor living, landscaping, and new construction — we coordinate your project from planning to execution.",
    cta1: "Request an Estimate", cta2: "Visualize Your Project",
  },
  services: {
    label: "Our Services", heading: "What Can We Help You With?",
    desc: "From interior renovations to outdoor living and permitting — one contractor coordinates every trade for your project.",
    more: "more",
    explore: "Learn more",
    cats: {
      remodeling: { title: "Remodeling", desc: "Kitchen, bathroom, whole-home renovations and interior transformations with premium finishes and modern design." },
      "multi-trade": { title: "Multi-Trade Services", desc: "Plumbing, electrical, HVAC, drywall, flooring, tile, painting, and structural & civil engineering coordination — all under one contractor." },
      "outdoor-living": { title: "Outdoor Living & Entertainment", desc: "Outdoor kitchens, pergolas, patios, pool areas and entertainment spaces designed for the Miami lifestyle." },
      landscape: { title: "Landscape Design & Exterior", desc: "Tropical landscaping, hardscape, irrigation, driveways, walkways, and decorative exterior elements." },
      construction: { title: "Construction & Development", desc: "Ground-up new construction, residential builds, commercial improvements, additions, and full project management." },
      permitting: { title: "Planning & Permitting", desc: "Permit coordination, code compliance, violation resolution, zoning, and pre-construction planning support." },
    }
  },
  insideOut: {
    label: "Full Property Coverage", heading: "One Contractor for Your Entire Property",
    sub: "Inside & Out.", interior: "Interior", exterior: "Exterior", cta: "Start Your Project",
  },
  inspiration: {
    label: "Visual Inspiration", heading: "Project Possibilities",
    desc: "Explore the types of transformations we can help you achieve. Images shown represent design inspiration and project possibilities.",
    badge: "Design Inspiration",
    cats: { "Modern Homes": "Modern Homes", "Kitchen Transformations": "Kitchen Transformations", "Bathroom Remodeling": "Bathroom Remodeling", "Outdoor Living": "Outdoor Living", "Landscape Design": "Landscape Design", "New Construction": "New Construction" }
  },
  outdoor: {
    label: "Outdoor Living", heading: "Outdoor Living Design & Construction",
    desc: "Transform your backyard into a complete outdoor living experience. From concept and design coordination to construction, finishes, and final installation.",
    items: ["Outdoor Kitchens", "Pergolas & Patios", "Pool Deck Areas", "BBQ & Fire Pits", "Entertainment Spaces", "Exterior Lighting"],
    cta: "Design Your Outdoor Space",
  },
  permit: {
    label: "Planning & Permitting", heading: "Planning & Permitting",
    desc: "From project feasibility and planning to permit coordination and construction readiness, we help organize the process before construction begins.",
    disclaimer: "Services are coordinated and performed by appropriately licensed trade professionals where required.",
    cta: "Request a Project Review",
    items: ["Permit Coordination & Applications", "Code Compliance Coordination", "Violation Resolution Coordination", "Zoning & Plan Review Coordination", "Pre-Construction Planning", "Project Feasibility", "Architectural & Engineering Coordination", "Inspection Coordination", "Construction Budgeting & Scheduling"],
  },
  visualizer: {
    label: "AI Powered", heading: "Visualize Your Project with AI",
    desc: "Upload a photo of your existing space and explore what your renovation could look like before construction begins.",
    steps: ["Upload Photo", "Select Type", "Select Style", "Preview"],
    upload: "Upload Your Space", uploadHint: "JPG, JPEG, PNG supported",
    selectType: "Select Project Type", selectStyle: "Select Design Style",
    generate: "Generate My Concept", comingSoon: "Coming Soon",
    comingDesc: "AI visualization will be available soon.", estimateAfter: "Request an estimate to discuss your",
    project: "project.", loveIt: "Love the Concept?", letUs: "Let Miami Multi-Trade help you build it.",
    tryAnother: "Try Another Space",
    disclaimer: "AI-generated conceptual visualizations are intended for inspiration and preliminary visualization only. Final design, dimensions, engineering, pricing, permitting and construction scope require professional review.",
  },
  howItWorks: {
    label: "Our Process", heading: "How It Works",
    steps: [
      { name: "Tell Us About Your Project", desc: "Share your vision, goals, and project requirements." },
      { name: "Upload Photos or Plans", desc: "Upload existing photos, architectural plans, or documents." },
      { name: "Project Review", desc: "Our team reviews scope, feasibility, and preliminary budget." },
      { name: "Site Visit", desc: "On-site evaluation and detailed measurements." },
      { name: "Detailed Estimate", desc: "Comprehensive proposal with scope, timeline, and pricing." },
      { name: "Planning & Permitting", desc: "Permit coordination and pre-construction planning if required." },
      { name: "Construction", desc: "Coordinated multi-trade execution from start to finish." },
    ],
  },
  completed: {
    label: "Our Work", heading: "Selected Completed Projects",
    desc: "Real transformations delivered by our team.",
    viewAll: "View All Completed Projects",
    fixFlipLabel: "Fix & Flip",
  },
  whyUs: {
    label: "Why Choose Us", heading: "Why Miami Multi-Trade",
    items: [
      { title: "One Point of Contact", desc: "A single project manager coordinates all trades and communications." },
      { title: "Multiple Trades", desc: "Plumbing, electrical, HVAC, and more — coordinated under one contractor." },
      { title: "Project Management", desc: "Professional oversight from planning through construction completion." },
      { title: "Permit Coordination", desc: "We navigate the complex permitting process so you don't have to." },
      { title: "Interior + Exterior", desc: "From kitchen renovations to outdoor living — one team, inside and out." },
      { title: "Miami Market Knowledge", desc: "Deep understanding of local codes, materials, and climate requirements." },
    ],
  },
  areas: { label: "Service Areas", heading: "Serving Miami-Dade County" },
  leadForm: {
    label: "Free Estimate", heading: "Request an Estimate",
    desc: "Tell us about your project and we will respond within 24 hours.",
    name: "Full Name", phone: "Phone", email: "Email", address: "Property Address",
    city: "City", zip: "ZIP Code", servicesReq: "Services Requested",
    budget: "Estimated Budget", timeline: "Desired Start Date", contactPref: "Preferred Contact",
    description: "Project Description", uploadLabel: "Upload Photos, Plans or Documents",
    uploadHint: "Choose Files (JPG, PNG, PDF — max 25MB each)",
    submit: "Request My Estimate", submitting: "Submitting...",
    success: "Thank you! We will contact you within 24 hours.",
    error: "Something went wrong. Please call us at 305-764-4388.",
    required: "Please fill in name, phone, and email.",
    select: "Select...",
    budgets: ["Under $10,000", "$10,000 – $25,000", "$25,000 – $50,000", "$50,000 – $100,000", "$100,000 – $250,000", "$250,000+", "Not Sure Yet"],
    timelines: ["Immediately", "1-3 Months", "3-6 Months", "6+ Months", "Planning Ahead"],
    contacts: ["Call", "Text", "Email", "WhatsApp"],
  },
  footer: {
    services: "Services", areas: "Service Areas", contactLabel: "Contact",
    rights: "All rights reserved.",
    disclaimer: "Services are coordinated and performed by appropriately licensed trade professionals where required.",
    operated: "Operated by",
    whatsapp: "Chat on WhatsApp",
    license: "Dviso Consulting works in collaboration with qualifier GC Zarabyzon Construction Inc. License Number: CGC1507148.",
  },
  compare: {
    existingIllustrative: "Illustrative Existing Scenario",
    existingReal: "Existing Conditions",
    concept: "AI Design Concept",
    notice: "AI-generated design visualization for illustration only. Not a completed project. Final scope and feasibility are subject to property assessment.",
    sectionNote: "Move the slider to compare, or tap \u201cView both\u201d to see each image on its own.",
    viewBoth: "View both",
    viewSlider: "Slider view",
    dragHint: "Drag to compare",
    exploreSimilar: "Explore a Similar Transformation",
    faqHeading: "Frequently Asked Questions",
    relatedHeading: "Related Services",
    backyardHeading: "See What's Possible Outdoors",
  },
  projects: {
    back: "Back to Home", label: "Our Work", heading: "Completed Projects",
    desc: "Browse our portfolio of completed renovations, fix & flip transformations, and construction projects across Miami-Dade County.",
    featured: "Featured Transformations", newConstruction: "New Construction",
    completedReno: "Completed Renovations", interiorRemodel: "Interior Remodeling",
    outdoorExterior: "Outdoor / Exterior", fixFlip: "Fix & Flip",
    gallery: "Construction Projects Gallery",
    before: "Before", after: "After",
  },
  emergency: {
    navBtn: "Emergency Services",
    badge: "24-Hour Response",
    heading: "Emergency Plumbing & Electrical Services in Miami",
    needHelp: "Need urgent help?",
    intro: "If you have an unexpected issue at your property, our team can coordinate emergency service requests for qualified trade professionals.",
    maxResponse: "Maximum response time: 24 hours.",
    tagline: "Fast response for urgent repair needs.",
    supporting: "Electrical, plumbing, HVAC / air conditioning, and other urgent repair needs.",
    requestBtn: "Request Emergency Service",
    callBtn: "Call Now",
    whatsappBtn: "Text / WhatsApp",
    pageTitle: "Emergency Services | Miami Multi-Trade",
    formHeading: "Emergency Service Request",
    formDesc: "Complete this form and our team will respond within a maximum of 24 hours.",
    categoriesLabel: "Type of Emergency",
    categories: [
      "Electrical Emergency", "Plumbing Emergency", "HVAC / Air Conditioning Emergency",
      "Water Leak / Pipe Break", "Drain / Sewer Issue", "Urgent Property Repair", "Other Emergency",
    ],
    name: "Full Name", phone: "Phone Number", email: "Email",
    address: "Property Address", city: "City", zip: "ZIP Code",
    describe: "Describe the Issue",
    started: "When did the issue start?",
    startedPlaceholder: "e.g. This morning, 2 days ago...",
    activeDamage: "Is this causing active damage?",
    yes: "Yes", no: "No",
    uploadLabel: "Upload Photos",
    uploadHint: "Choose Files (JPG, PNG, PDF — max 25MB each)",
    contactPref: "Preferred Contact Method",
    contacts: ["Call", "Text", "Email", "WhatsApp"],
    submit: "Submit Emergency Request", submitting: "Submitting...",
    notice: "We aim to respond to emergency requests within a maximum of 24 hours.",
    warning911: "If this is a life-threatening emergency or immediate fire hazard, call 911.",
    success: "Emergency request received. Our team will contact you as soon as possible, within 24 hours maximum.",
    error: "Something went wrong. Please call us now at 305-764-4388.",
    required: "Please fill in your name and phone number.",
    selectCategory: "Please select the type of emergency.",
    back: "Back to Home",
  },
};

const es = {
  nav: {
    services: "Servicios", outdoor: "Espacios Exteriores", permitting: "Planificación y Permisos",
    visualizer: "Visualizador IA", projects: "Proyectos", contact: "Contacto", estimate: "Solicitar Estimado",
    technology: "Tecnología",
  },
  hero: {
    badge: "Construcción · Remodelación · Planificación · Permisos",
    heading1: "MIAMI", heading2: "MULTI-TRADE",
    tagline: "UN CONTRATISTA. MÚLTIPLES OFICIOS. INTERIOR Y EXTERIOR.",
    desc: "Desde renovaciones completas hasta espacios exteriores, paisajismo y construcción nueva — coordinamos su proyecto desde la planificación hasta la ejecución.",
    cta1: "Solicitar un Estimado", cta2: "Visualice Su Proyecto",
  },
  services: {
    label: "Nuestros Servicios", heading: "¿En Qué Podemos Ayudarle?",
    desc: "Desde renovaciones interiores hasta espacios exteriores y permisos — un contratista coordina cada oficio para su proyecto.",
    more: "más",
    explore: "Ver más",
    cats: {
      remodeling: { title: "Remodelación", desc: "Remodelación de cocinas, baños, casas completas y transformaciones interiores con acabados premium y diseño moderno." },
      "multi-trade": { title: "Servicios Multi-Oficio", desc: "Plomería, electricidad, aire acondicionado, paneles, pisos, azulejos, pintura y coordinación de ingeniería estructural y civil — todo bajo un solo contratista." },
      "outdoor-living": { title: "Espacios Exteriores", desc: "Cocinas al aire libre, pérgolas, patios, áreas de piscina y espacios de entretenimiento diseñados para el estilo de vida de Miami." },
      landscape: { title: "Diseño de Paisajismo", desc: "Paisajismo tropical, pavimentación, sistemas de riego, entradas, caminos y elementos decorativos exteriores." },
      construction: { title: "Construcción y Desarrollo", desc: "Construcción nueva, edificaciones residenciales, mejoras comerciales, adiciones y gestión completa de proyectos." },
      permitting: { title: "Planificación y Permisos", desc: "Coordinación de permisos, cumplimiento de códigos, resolución de violaciones, zonificación y planificación pre-construcción." },
    }
  },
  insideOut: {
    label: "Cobertura Total", heading: "Un Contratista Para Toda Su Propiedad",
    sub: "Interior y Exterior.", interior: "Interior", exterior: "Exterior", cta: "Inicie Su Proyecto",
  },
  inspiration: {
    label: "Inspiración Visual", heading: "Posibilidades de Proyecto",
    desc: "Explore los tipos de transformaciones que podemos ayudarle a lograr. Las imágenes representan inspiración de diseño y posibilidades de proyecto.",
    badge: "Inspiración de Diseño",
    cats: { "Modern Homes": "Casas Modernas", "Kitchen Transformations": "Transformaciones de Cocina", "Bathroom Remodeling": "Remodelación de Baños", "Outdoor Living": "Espacios Exteriores", "Landscape Design": "Diseño de Paisajismo", "New Construction": "Construcción Nueva" }
  },
  outdoor: {
    label: "Espacios Exteriores", heading: "Diseño y Construcción de Espacios Exteriores",
    desc: "Transforme su patio en una experiencia completa de vida al aire libre. Desde el concepto y coordinación del diseño hasta la construcción, acabados e instalación final.",
    items: ["Cocinas al Aire Libre", "Pérgolas y Patios", "Áreas de Piscina", "Parrillas y Fogatas", "Espacios de Entretenimiento", "Iluminación Exterior"],
    cta: "Diseñe Su Espacio Exterior",
  },
  permit: {
    label: "Planificación y Permisos", heading: "Planificación y Permisos",
    desc: "Desde la viabilidad del proyecto y la planificación hasta la coordinación de permisos y la preparación para la construcción, ayudamos a organizar el proceso antes de que comience la construcción.",
    disclaimer: "Los servicios son coordinados y realizados por profesionales debidamente licenciados cuando es requerido.",
    cta: "Solicitar Revisión de Proyecto",
    items: ["Coordinación y Solicitud de Permisos", "Coordinación de Cumplimiento de Códigos", "Coordinación de Resolución de Violaciones", "Coordinación de Zonificación y Revisión", "Planificación Pre-Construcción", "Viabilidad del Proyecto", "Coordinación Arquitectónica e Ingeniería", "Coordinación de Inspecciones", "Presupuesto y Programación"],
  },
  visualizer: {
    label: "Potenciado por IA", heading: "Visualice Su Proyecto con IA",
    desc: "Suba una foto de su espacio actual y explore cómo podría verse su renovación antes de comenzar la construcción.",
    steps: ["Subir Foto", "Tipo de Proyecto", "Estilo", "Vista Previa"],
    upload: "Suba Su Espacio", uploadHint: "JPG, JPEG, PNG soportados",
    selectType: "Seleccione Tipo de Proyecto", selectStyle: "Seleccione Estilo de Diseño",
    generate: "Generar Mi Concepto", comingSoon: "Próximamente",
    comingDesc: "La visualización con IA estará disponible pronto.", estimateAfter: "Solicite un estimado para discutir su proyecto de",
    project: ".", loveIt: "¿Le Gusta el Concepto?", letUs: "Permita que Miami Multi-Trade le ayude a construirlo.",
    tryAnother: "Intentar Otro Espacio",
    disclaimer: "Las visualizaciones conceptuales generadas por IA son solo para inspiración y visualización preliminar. El diseño final, dimensiones, ingeniería, precios, permisos y alcance de construcción requieren revisión profesional.",
  },
  howItWorks: {
    label: "Nuestro Proceso", heading: "Cómo Funciona",
    steps: [
      { name: "Cuéntenos Sobre Su Proyecto", desc: "Comparta su visión, objetivos y requisitos del proyecto." },
      { name: "Suba Fotos o Planos", desc: "Suba fotos existentes, planos arquitectónicos o documentos." },
      { name: "Revisión del Proyecto", desc: "Nuestro equipo revisa alcance, viabilidad y presupuesto preliminar." },
      { name: "Visita al Sitio", desc: "Evaluación en sitio y mediciones detalladas." },
      { name: "Estimado Detallado", desc: "Propuesta integral con alcance, cronograma y precios." },
      { name: "Planificación y Permisos", desc: "Coordinación de permisos y planificación pre-construcción si es necesario." },
      { name: "Construcción", desc: "Ejecución coordinada multi-oficio de inicio a fin." },
    ],
  },
  completed: {
    label: "Nuestro Trabajo", heading: "Proyectos Completados Seleccionados",
    desc: "Transformaciones reales entregadas por nuestro equipo.",
    viewAll: "Ver Todos los Proyectos",
    fixFlipLabel: "Fix & Flip",
  },
  whyUs: {
    label: "Por Qué Elegirnos", heading: "Por Qué Miami Multi-Trade",
    items: [
      { title: "Un Solo Punto de Contacto", desc: "Un gerente de proyecto coordina todos los oficios y comunicaciones." },
      { title: "Múltiples Oficios", desc: "Plomería, electricidad, aire acondicionado y más — coordinados bajo un contratista." },
      { title: "Gestión de Proyectos", desc: "Supervisión profesional desde la planificación hasta la finalización." },
      { title: "Coordinación de Permisos", desc: "Navegamos el complejo proceso de permisos para que usted no tenga que hacerlo." },
      { title: "Interior + Exterior", desc: "Desde remodelación de cocinas hasta espacios exteriores — un equipo, interior y exterior." },
      { title: "Conocimiento del Mercado", desc: "Profundo entendimiento de códigos locales, materiales y requisitos climáticos de Miami." },
    ],
  },
  areas: { label: "Áreas de Servicio", heading: "Sirviendo al Condado Miami-Dade" },
  leadForm: {
    label: "Estimado Gratuito", heading: "Solicite un Estimado",
    desc: "Cuéntenos sobre su proyecto y le responderemos en 24 horas.",
    name: "Nombre Completo", phone: "Teléfono", email: "Correo Electrónico", address: "Dirección de la Propiedad",
    city: "Ciudad", zip: "Código Postal", servicesReq: "Servicios Solicitados",
    budget: "Presupuesto Estimado", timeline: "Fecha de Inicio Deseada", contactPref: "Contacto Preferido",
    description: "Descripción del Proyecto", uploadLabel: "Subir Fotos, Planos o Documentos",
    uploadHint: "Elegir Archivos (JPG, PNG, PDF — máx 25MB cada uno)",
    submit: "Solicitar Mi Estimado", submitting: "Enviando...",
    success: "¡Gracias! Le contactaremos en 24 horas.",
    error: "Algo salió mal. Llámenos al 305-764-4388.",
    required: "Por favor complete nombre, teléfono y correo.",
    select: "Seleccionar...",
    budgets: ["Menos de $10,000", "$10,000 – $25,000", "$25,000 – $50,000", "$50,000 – $100,000", "$100,000 – $250,000", "$250,000+", "No Estoy Seguro"],
    timelines: ["Inmediatamente", "1-3 Meses", "3-6 Meses", "6+ Meses", "Planificando"],
    contacts: ["Llamada", "Texto", "Correo", "WhatsApp"],
  },
  footer: {
    services: "Servicios", areas: "Áreas de Servicio", contactLabel: "Contacto",
    rights: "Todos los derechos reservados.",
    disclaimer: "Los servicios son coordinados y realizados por profesionales debidamente licenciados cuando es requerido.",
    operated: "Operado por",
    whatsapp: "Chatea por WhatsApp",
    license: "Dviso Consulting trabaja en colaboración con el calificador GC Zarabyzon Construction Inc. Número de Licencia: CGC1507148.",
  },
  compare: {
    existingIllustrative: "Escenario Existente Ilustrativo",
    existingReal: "Condiciones Existentes",
    concept: "Concepto de Diseño con IA",
    notice: "Visualización de diseño generada por IA, solo con fines ilustrativos. No es un proyecto terminado. El alcance y la viabilidad finales están sujetos a una evaluación de la propiedad.",
    sectionNote: "Mueva el control para comparar, o toque \u201cVer ambas\u201d para ver cada imagen por separado.",
    viewBoth: "Ver ambas",
    viewSlider: "Vista deslizante",
    dragHint: "Arrastre para comparar",
    exploreSimilar: "Explorar una Transformación Similar",
    faqHeading: "Preguntas Frecuentes",
    relatedHeading: "Servicios Relacionados",
    backyardHeading: "Vea Lo Que Es Posible en el Exterior",
  },
  projects: {
    back: "Volver al Inicio", label: "Nuestro Trabajo", heading: "Proyectos Completados",
    desc: "Explore nuestro portafolio de renovaciones completadas, transformaciones fix & flip y proyectos de construcción en el Condado Miami-Dade.",
    featured: "Transformaciones Destacadas", newConstruction: "Construcción Nueva",
    completedReno: "Renovaciones Completadas", interiorRemodel: "Remodelación Interior",
    outdoorExterior: "Exterior", fixFlip: "Fix & Flip",
    gallery: "Galería de Proyectos de Construcción",
    before: "Antes", after: "Después",
  },
  emergency: {
    navBtn: "Servicios de Emergencia",
    badge: "Respuesta en 24 Horas",
    heading: "Servicios de Emergencia de Plomería y Electricidad en Miami",
    needHelp: "¿Necesita ayuda urgente?",
    intro: "Si tiene un problema inesperado en su propiedad, nuestro equipo puede coordinar solicitudes de servicio de emergencia con profesionales calificados.",
    maxResponse: "Tiempo máximo de respuesta: 24 horas.",
    tagline: "Respuesta rápida para reparaciones urgentes.",
    supporting: "Electricidad, plomería, aire acondicionado / HVAC y otras reparaciones urgentes.",
    requestBtn: "Solicitar Servicio de Emergencia",
    callBtn: "Llamar Ahora",
    whatsappBtn: "Texto / WhatsApp",
    pageTitle: "Servicios de Emergencia | Miami Multi-Trade",
    formHeading: "Solicitud de Servicio de Emergencia",
    formDesc: "Complete este formulario y nuestro equipo responderá en un máximo de 24 horas.",
    categoriesLabel: "Tipo de Emergencia",
    categories: [
      "Emergencia eléctrica", "Emergencia de plomería", "Emergencia de aire acondicionado / HVAC",
      "Fuga de agua / tubería rota", "Problema de drenaje / alcantarillado", "Reparación urgente de propiedad", "Otra emergencia",
    ],
    name: "Nombre Completo", phone: "Número de Teléfono", email: "Correo Electrónico",
    address: "Dirección de la Propiedad", city: "Ciudad", zip: "Código Postal",
    describe: "Describa el Problema",
    started: "¿Cuándo comenzó el problema?",
    startedPlaceholder: "ej. Esta mañana, hace 2 días...",
    activeDamage: "¿Está causando daño activo?",
    yes: "Sí", no: "No",
    uploadLabel: "Subir Fotos",
    uploadHint: "Elegir Archivos (JPG, PNG, PDF — máx 25MB cada uno)",
    contactPref: "Método de Contacto Preferido",
    contacts: ["Llamada", "Texto", "Correo", "WhatsApp"],
    submit: "Enviar Solicitud de Emergencia", submitting: "Enviando...",
    notice: "Nuestro objetivo es responder a las solicitudes de emergencia en un máximo de 24 horas.",
    warning911: "Si es una emergencia que amenaza la vida o un peligro de incendio inmediato, llame al 911.",
    success: "Solicitud de emergencia recibida. Nuestro equipo le contactará lo antes posible, en un máximo de 24 horas.",
    error: "Algo salió mal. Llámenos ahora al 305-764-4388.",
    required: "Por favor complete su nombre y número de teléfono.",
    selectCategory: "Por favor seleccione el tipo de emergencia.",
    back: "Volver al Inicio",
  },
};

const translations = { en, es };

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('mmt_lang') || 'en');

  useEffect(() => { localStorage.setItem('mmt_lang', lang); }, [lang]);

  const t = translations[lang] || en;

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}
